<div class="tw-flex tw-flex-col tw-gap-1">
  @if (label) {
    <div class="tw-flex tw-gap-1 tw-font-bold tw-text-dt-text-tertiary tw-z-10 tw-text-[13px]">
      <span class="tw-whitespace-nowrap" [ngClass]="labelClass">{{ label }}</span>
      <span *ngIf="isRequired" class="tw-text-sm">*</span>
    </div>
  }
  <div
    #ddWrapper
    class="tw-relative tw-w-full tw-items-center"
    [ngClass]="controlHeightClass || (compact ? 'tw-h-[24px]' : 'tw-h-12')"
    appClickOutside
    (clickOutside)="onClickOutside($event)"
    [appScrollLock]="lockPageScroll && isExpanded"
    [attr.data-testid]="
      'dropdown' +
      (label ? '-' + label.toLowerCase() : testLabel ? '-' + testLabel.toLowerCase() : '')
    "
  >
    <!--
      tw-rounded-xl below covers three cases (enabled+panelRadius==='xl', any disabled+non-compact,
      and disabled+compact) in one key. NgClass toggles each individual class token as it walks the
      object's keys in order, so if a class name were split across two keys — like the old
      'tw-rounded-xl' here plus a second 'tw-rounded-xl tw-px-2 tw-text-sm' key further down for the
      compact+disabled case — whichever key is declared last wins for that shared token regardless
      of which key's own condition is true (the compact+disabled fix used to sit below this one, so
      its "false" case silently removed the tw-rounded-xl this key had just added for every
      non-compact disabled dropdown). No !important needed: every competing radius class here is
      isEnabled-gated, so it can never be present on the element at the same time as this one.
      Same rule for 'tw-px-2 tw-text-sm' (#5442): the compact enabled and compact disabled cases
      share one key, rather than each repeating those tokens, or the disabled key's false case
      strips them from every compact enabled dropdown.
    -->
    <div
      #sizeDetector
      #ddTrigger
      cdkOverlayOrigin
      #ddOrigin="cdkOverlayOrigin"
      [attr.aria-label]="label || placeholder"
      aria-haspopup="listbox"
      [attr.aria-expanded]="isExpanded"
      [attr.aria-controls]="ddPanelId"
      class="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-between"
      [class]="customShadowClass"
      [ngClass]="{
        'tw-cursor-pointer': isEnabled,
        'tw-cursor-not-allowed': !isEnabled,
        'tw-opacity-50': !isEnabled,
        '!tw-shadow-elevation-input': !isEnabled && !compact,
        'tw-bg-dt-surface-card': !compact && isEnabled,
        'tw-bg-dt-surface-disabled': !isEnabled,
        'tw-text-dt-text-secondary': !isEnabled,
        'border-gradient-neuomorphic': showGradientBorder && !compact && isEnabled,
        'tw-rounded-2xl': panelRadius === 'default' && !compact && isEnabled && !joinPanelOnExpand,
        'tw-rounded-full': panelRadius === 'rounded' && !compact && isEnabled && !joinPanelOnExpand,
        'tw-rounded-xl':
          (!compact && !joinPanelOnExpand && (!isEnabled || panelRadius === 'xl')) ||
          (compact && !isEnabled),
        '!tw-rounded-2xl': joinPanelOnExpand && !isExpanded && isEnabled,
        '!tw-rounded-t-2xl !tw-rounded-b-none': joinPanelOnExpand && isExpanded && isEnabled,
        'tw-px-3': divIsLessThan100px && !compact,
        'tw-px-6': !divIsLessThan100px && !compact,
        'tw-rounded': compact && isEnabled && !joinPanelOnExpand && customShadowClass === '',
        'tw-px-2 tw-text-sm':
          compact && (!isEnabled || (!joinPanelOnExpand && customShadowClass === '')),
        '!tw-shadow-button !tw-bg-color-surface-input':
          compact && isEnabled && customShadowClass === '',
        'tw-shadow-modal-strong':
          panelStyle === 'default' && customShadowClass === '' && !compact && isEnabled,
        'tw-shadow-elevation-input':
          panelStyle === 'mat' && customShadowClass === '' && !compact && isEnabled,
        'hover-border-past': isEnabled && currentView === View.PAST,
        'hover-border-future': isEnabled && currentView === View.FUTURE,
        'hover-border-today': isEnabled && currentView === View.TODAY,
        'border-active-past': isExpanded && currentView === View.PAST,
        'border-active-future': isExpanded && currentView === View.FUTURE,
        'border-active-today': isExpanded && currentView === View.TODAY,
        'flat-trigger': flat && isEnabled,
        'flat-trigger-active': flat && isEnabled && isExpanded,
        'border-error': isInvalid() && showGradientBorder && isEnabled,
        'input-error-border': isInvalid() && !showGradientBorder,
      }"
      appKeyboardActivatable
      [appKeyboardActivatableDisabled]="!isEnabled"
      [appKeyboardActivatableExpanded]="isExpanded"
      (appKeyboardActivatableActivate)="toggleExpand()"
      (click)="isEnabled && toggleExpand()"
    >
      <span
        [ngClass]="{
          'tw-opacity-60': !selectedOption && placeholder && isEnabled,
          'tw-flex-1 tw-text-center': centerSelected,
          'tw-form-field-text': !compact,
        }"
      >
        {{ getSelectedOptionDisplay() || placeholder }}
      </span>
      <icon
        [name]="'down-arrow'"
        [size]="arrowSize ?? (compact ? 3 : 6)"
        [currentColor]="'dt-text-secondary'"
        [disableArrowOverride]="true"
        class="tw-right-3"
        [class.tw-invisible]="!isEnabled"
      ></icon>
    </div>
    <div
      *ngIf="!detachPanel && isExpanded && isEnabled"
      #ddPanel
      [id]="ddPanelId"
      role="listbox"
      tabindex="-1"
      [attr.aria-label]="label || placeholder"
      (keydown)="onListboxKeydown($event)"
      (focusout)="onListboxFocusOut($event)"
      class="tw-absolute tw-z-20 tw-w-full tw-min-w-fit tw-p-2"
      [class]="panelClass"
      [ngClass]="{
        'flat-dropdown-panel': flat,
        'border-gradient-neuomorphic glass-dropdown-panel': !flat,
        'tw-mt-[3px] tw-rounded-2xl': !joinPanelOnExpand && panelClass === '',
        'tw-mt-0 tw-rounded-b-2xl tw-rounded-t-none': joinPanelOnExpand,
        'border-active-past': !flat && currentView === View.PAST,
        'border-active-future': !flat && currentView === View.FUTURE,
        'border-active-today': !flat && currentView === View.TODAY,
      }"
    >
      <ng-container *ngTemplateOutlet="dropdownPanel"></ng-container>
    </div>
    @if (detachPanel) {
      <ng-template
        cdkConnectedOverlay
        [cdkConnectedOverlayOrigin]="ddOrigin"
        [cdkConnectedOverlayOpen]="isExpanded && isEnabled"
        [cdkConnectedOverlayWidth]="ddWrapper.offsetWidth"
        [cdkConnectedOverlayPositions]="overlayPositions"
        [cdkConnectedOverlayPush]="true"
        [cdkConnectedOverlayScrollStrategy]="overlayScrollStrategy"
        (overlayOutsideClick)="onOverlayOutsideClick($event, ddWrapper)"
      >
        <div
          #ddPanel
          [id]="ddPanelId"
          role="listbox"
          tabindex="-1"
          [attr.aria-label]="label || placeholder"
          (keydown)="onListboxKeydown($event)"
          (focusout)="onListboxFocusOut($event)"
          class="tw-z-20 tw-w-full tw-min-w-fit tw-p-2"
          [class]="panelClass"
          [ngClass]="{
            'flat-dropdown-panel': flat,
            'border-gradient-neuomorphic glass-dropdown-panel': !flat,
            'tw-rounded-2xl': !joinPanelOnExpand && panelClass === '',
            'tw-rounded-b-2xl tw-rounded-t-none': joinPanelOnExpand,
          }"
        >
          <ng-container *ngTemplateOutlet="dropdownPanel"></ng-container>
        </div>
      </ng-template>
    }
  </div>
  @if (isInvalid()) {
    <span class="tw-whitespace-nowrap tw-text-dt-text-error tw-text-xs">{{
      "thisFieldIsRequired" | i18n
    }}</span>
  }
</div>

<ng-template #dropdownPanel>
  <!--
    APG Listbox pattern (#4657 follow-up to #4511): a single Tab stop via roving tabindex, plus
    Up/Down/Home/End navigation on the listbox container (onListboxKeydown) and focus-out
    auto-close (onListboxFocusOut) since Tab isn't routed through appClickOutside. Before #4511
    these options had no tabindex at all and were completely unreachable by keyboard; before this
    fix every option was its own Tab stop rather than one for the whole composite.
  -->
  <div
    appGlossScroll
    role="presentation"
    class="tw-w-full tw-max-h-[256px] tw-overflow-y-auto tw-overscroll-contain"
  >
    @for (option of mappedOptions; track option.realValue; let i = $index) {
      <div
        #ddOption
        role="option"
        [attr.aria-selected]="option.realValue === selectedOption"
        [attr.tabindex]="isDisabled(option.realValue) ? -1 : i === activeOptionIndex ? 0 : -1"
        [attr.aria-disabled]="isDisabled(option.realValue) || null"
        class="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-gap-2 tw-rounded-xl focus-visible:tw-relative focus-visible:tw-z-10 focus-visible:-tw-outline-offset-2"
        [ngClass]="{
          'dropdown-option-flat': flat,
          'dropdown-option-glass': !flat,
          'tw-h-12 tw-px-5': !compact,
          'tw-h-[32px] tw-px-3 tw-text-sm': compact,
          selected: flat && option.realValue === selectedOption,
          past: !flat && option.realValue === selectedOption && currentView === View.PAST,
          today: !flat && option.realValue === selectedOption && currentView === View.TODAY,
          future: !flat && option.realValue === selectedOption && currentView === View.FUTURE,
          'tw-opacity-50': isDisabled(option.realValue),
        }"
        (click)="selectOption(option.realValue)"
        (keydown.enter)="selectOption(option.realValue)"
        (keydown.space)="$event.preventDefault(); selectOption(option.realValue)"
      >
        <span>{{ option.optionDisplayValue }}</span>
        @if (option.suffixText) {
          <span class="tw-font-bold" [ngClass]="option.suffixClass || ''">{{
            option.suffixText
          }}</span>
        }
      </div>
    }
  </div>
</ng-template>
